import { useCallback, useEffect, useRef, useState } from 'react';

import { useToastStore } from '@pkg/components/Toast/useToastStore';
import {
    type AddToLineupOptions,
    type CollectionEntryType,
    useAddLineupItemMutation,
    useAppDispatch,
    useBreakpoints,
    useQueryInvalidation,
} from '@pkg/hooks';

import { type Competition } from '@app/client/common/structs';
import { COMPETITIONS_CACHE, COMPETITIONS_LEADERBOARD_CACHE } from '@app/client/resources/competitions';
import { useFantasyTracking } from '@app/pages/FantasyWishlist/hooks';
import {
    useFantasyAutopickedStore,
    useFantasyLineupStore,
    useFantasyMetadataStore,
    useFantasySummaryStore,
    useFantasyTeamsStore,
} from '@app/store/zustand';

interface NewItemProps {
    item: CollectionEntryType;
    options?: AddToLineupOptions;
    firstBuy?: boolean;
}

export function useAddLineupItem() {
    const invalidateLineupQueries = useQueryInvalidation([COMPETITIONS_CACHE, COMPETITIONS_LEADERBOARD_CACHE]);
    const [customGame, setCustomGame] = useState<Competition>();
    const { currentGameWishlistId, setCurrentGameWishlistId, currentGame } = useFantasyMetadataStore();
    const { setFirstBuy } = useFantasySummaryStore();
    const { addAutoPickedItem } = useFantasyAutopickedStore();
    const { addLineupItem, setNewItemAdded } = useFantasyLineupStore();
    const { userTeam } = useFantasyTeamsStore();
    const { trackLineupSubmitted } = useFantasyTracking();
    const { mutateAsync: addItem, isPending: isLoadingAddition } = useAddLineupItemMutation();
    const { addToast } = useToastStore();
    const { isMobile } = useBreakpoints();

    const dispatch = useAppDispatch();

    const addItemToLineup = useCallback(
        async ({ item, options, firstBuy }: NewItemProps) => {
            const { isAutoPicked } = options || {
                isAutoPicked: false,
            };

            if (item.quantity) {
                const value = {
                    network: item.chain,
                    Collection: { slug: item.collectionSlug },
                    quantity: item.quantity,
                    confirmed: true,
                    competitionId: customGame?.id || currentGame?.id || '',
                    wishlistId: currentGameWishlistId,
                    position: item.position,
                    // teamId: userTeam?.id,
                };

                return await addItem({ value }).then((r) => {
                    if (!r?.ok && r?.error) {
                        if (options?.onError) {
                            return options.onError();
                        }
                    }

                    if (r?.ok) {
                        const { wishlist_id } = r.value;
                        if (wishlist_id && wishlist_id !== currentGameWishlistId) {
                            setCurrentGameWishlistId(wishlist_id);
                        }

                        if (firstBuy) {
                            invalidateLineupQueries();
                            trackLineupSubmitted();
                            setFirstBuy(item);
                        }

                        if (options?.pessimistic) {
                            addLineupItem({
                                ...r.value.item,
                                slug: item.collectionSlug,
                                quantity: item.quantity,
                                chain: item.chain,
                                confirmed: options?.isConfirmed,
                                position: item.position,
                            });
                        }

                        if (!options?.isUpdate && options?.isConfirmed && !isMobile) {
                            addToast({
                                emoji: '🚀',
                                message: item.collectionName
                                    ? `${item.collectionName} added to lineup!`
                                    : 'Added to lineup!',
                            });
                        }

                        if (isAutoPicked) {
                            addAutoPickedItem({
                                slug: item.collectionSlug,
                                fw_slug: currentGameWishlistId || '',
                            });
                        }
                        if (options?.callback) {
                            options.callback();
                        }
                    }
                    return r;
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            addItem,
            currentGame?.id,
            currentGameWishlistId,
            customGame?.id,
            dispatch,
            invalidateLineupQueries,
            trackLineupSubmitted,
            userTeam?.id,
        ],
    );

    const [queuedElements, setQueuedElements] = useState<NewItemProps[]>([]);
    const addingItem = useRef<NewItemProps | null>(null);

    useEffect(() => {
        if (isLoadingAddition || addingItem.current) return;

        const itemToAdd = queuedElements[0];
        const handleNext = () => {
            addingItem.current = null;
            setQueuedElements((curr) =>
                curr.filter((itemData) => itemData.item.collectionSlug !== itemToAdd.item.collectionSlug),
            );
        };

        if (itemToAdd) {
            addingItem.current = itemToAdd;
            if (itemToAdd.options?.isConfirmed) {
                addItemToLineup(itemToAdd)
                    .then((r) => {
                        if (r?.ok) {
                            itemToAdd.options?.callback?.();
                        } else {
                            itemToAdd.options?.onError?.();
                        }
                    })
                    .then(invalidateLineupQueries)
                    .finally(handleNext);
            } else {
                handleNext();
            }
        }
    }, [addItemToLineup, invalidateLineupQueries, isLoadingAddition, queuedElements]);

    return (item: CollectionEntryType, options?: AddToLineupOptions) => {
        if (options?.customGame && options?.customGame !== customGame) {
            setCustomGame(options?.customGame);
        }

        if (!options?.pessimistic) {
            addLineupItem({
                slug: item.collectionSlug,
                quantity: item.quantity,
                chain: item.chain,
                confirmed: options?.isConfirmed,
                position: item.position,
            });
        }

        setNewItemAdded(item.collectionSlug); // this is to auto-popup the mobile controls
        const newItem = { item, options, firstBuy: !currentGameWishlistId && options?.isConfirmed };
        setQueuedElements((queuedElements) => [...queuedElements, newItem]);
    };
}
